// Types
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { getMenuProps } from 'components/ContextMenu';
import LoadingWrapper from 'components/LoadingWrapper';
import dayjs from 'dayjs';
import { useDefaultTableProps } from 'hooks/useDefaultTableProps';
import { useDeleteConfirmation } from 'hooks/useDeleteConfirmation';
import { useReportingHeatmapPage } from 'hooks/useFeatureFlag';
import { useRowProps } from 'hooks/useRowProps';
import { useTableTitle } from 'hooks/useTableTitle';
import { getText } from 'i18n/intl';
// Slices
import {
  requestObjectTypes,
  selectObjectTypesList,
  selectObjectTypesMap,
} from 'slices/objectTypes.slice';
import {
  checkFirstReportTemplateExist,
  createReportTemplate,
  deleteReportTemplate,
  duplicateReportTemplate,
  reportTemplateMapSelector,
  reportTemplatesLoading,
  reportTemplatesSelector,
  selectedIdsSelector,
  setFirstReportTemplate,
  setSecondReportTemplate,
} from 'slices/reportTemplate.slice';
import { useAppDispatch, useAppSelector } from 'slices/store';
import { timeZonesSelector } from 'slices/timeZone.slice';
// Utils
import { createTypeOptions } from 'utils/objectTypes';
import { matchString } from 'utils/stringUtils';
import { EReportTemplateType, type TReportTemplate } from '@timeedit/types';
import {
  Button,
  EditableTable,
  Filter,
  TEEFilterType,
  type TEIFilter,
  type TEIFilterField,
} from '@timeedit/ui-components';
import { Dropdown, type MenuProps } from '@timeedit/ui-components/antd';
// Styles
import './TableList.scss';

export interface IRowData {
  id: string;
  name: string;
  objectTypes: string;
  templateType: EReportTemplateType;
  dateRangeStart?: Date;
  dateRangeEnd?: Date;
  timezone: string;
}

interface Props {
  confirmDiscardPaddingChanges: (
    isFirstDrawer: boolean,
    actionFn: () => void
  ) => void;
}

const DataTable = ({ confirmDiscardPaddingChanges }: Props) => {
  const dispatch = useAppDispatch();

  const reportTemplates: TReportTemplate[] = useAppSelector(
    reportTemplatesSelector
  );
  const reportTemplateMap: Record<number, TReportTemplate> = useAppSelector(
    reportTemplateMapSelector
  );
  const [highlightIds, setHighlightIds] = useState<string[]>([]);
  const selectedIds = useAppSelector(selectedIdsSelector);
  const loading = useAppSelector(reportTemplatesLoading);
  const objectTypesMap = useAppSelector(selectObjectTypesMap);
  const objectTypes = useAppSelector(selectObjectTypesList);
  const timezones = useAppSelector(timeZonesSelector);

  const enableHeatmapPage = useReportingHeatmapPage();

  const templateTypeOptions = useMemo(
    () =>
      Object.values(EReportTemplateType)
        .filter((value) => {
          if (value === EReportTemplateType.HEATMAP && !enableHeatmapPage) {
            return false;
          }
          return true;
        })
        .map((value) => ({
          value,
          key: value,
          label: getText(value),
        })),
    [enableHeatmapPage]
  );

  useEffect(() => {
    setTimeout(() => {
      setHighlightIds(selectedIds);
    }, 100);
  }, [selectedIds.join('-')]);

  const [filterSettings, setFilterSettings] = useState<TEIFilter>({
    filters: {},
    freeTextSearch: '',
  });

  const [confirmDelete, contextHolder] = useDeleteConfirmation();

  const objectTypeOptions = useMemo(
    () => createTypeOptions(objectTypes),
    [objectTypes]
  );

  const filterData: IRowData[] = useMemo(() => {
    const filterTemplates = reportTemplates.filter((template) => {
      let matchedObjectType = true;
      let matchedText = true;
      let matchedTemplateType = true;
      const {
        filters: { objectType, templateType },
        freeTextSearch,
      } = filterSettings;

      if (objectType) {
        matchedObjectType =
          template.targetObjectType === +objectType ||
          template.capacityObject === +objectType;
      }

      if (templateType) {
        matchedTemplateType = template.templateType === templateType;
      }

      if (freeTextSearch) {
        matchedText = matchString(template.name, freeTextSearch);
      }

      return matchedObjectType && matchedText && matchedTemplateType;
    });

    return filterTemplates.map((item) => {
      const targetObjectTypeName = objectTypesMap[item.targetObjectType]?.name;
      const capacityObjectName = objectTypesMap[item.capacityObject]?.name;

      return {
        id: item.id,
        objectTypes: [targetObjectTypeName, capacityObjectName]
          .filter(Boolean)
          .join(', '),
        name: item.name || 'New report template',
        templateType: item.templateType,
        dateRangeStart: item.dateRangeStart,
        dateRangeEnd: item.dateRangeEnd,
        timezone: item.timezone,
      };
    });
  }, [reportTemplates, filterSettings, objectTypesMap]);

  const onCreate = useCallback(
    (templateType: EReportTemplateType) => {
      confirmDiscardPaddingChanges(true, () => {
        const defaultTimezone =
          timezones.find((tz) => tz.isDefault) || timezones?.[0];
        dispatch(
          createReportTemplate({
            templateType,
            timezone: defaultTimezone?.identifiers?.[0] || 'Etc/UTC',
          })
        );
      });
    },
    [confirmDiscardPaddingChanges, dispatch, timezones]
  );

  const onDuplicate = useCallback(
    (record: IRowData) => {
      confirmDiscardPaddingChanges(true, () =>
        dispatch(duplicateReportTemplate(reportTemplateMap[record.id]))
      );
    },
    [reportTemplateMap]
  );

  const onCompare = useCallback(
    (record: IRowData) => {
      const firstReportTemplateExist = dispatch(
        checkFirstReportTemplateExist()
      );
      if (!firstReportTemplateExist) {
        return dispatch(setFirstReportTemplate(reportTemplateMap[record.id]));
      }
      confirmDiscardPaddingChanges(false, () =>
        dispatch(setSecondReportTemplate(reportTemplateMap[record.id]))
      );
    },
    [reportTemplateMap]
  );

  const onRowClick = useCallback(
    ({ id }: IRowData) => {
      confirmDiscardPaddingChanges(true, () =>
        dispatch(setFirstReportTemplate(reportTemplateMap[id]))
      );
    },
    [reportTemplateMap]
  );

  const onRow = useRowProps(onRowClick, highlightIds);

  const onDelete = useCallback(
    (record: IRowData) =>
      confirmDelete(record.name, () =>
        dispatch(deleteReportTemplate(record.id))
      ),
    [confirmDelete, dispatch]
  );
  const checkDrawerOpen = useCallback(
    () => Boolean(dispatch(checkFirstReportTemplateExist())),
    [dispatch]
  );

  const menuProps = useCallback(
    (record: IRowData) =>
      getMenuProps<IRowData>({
        record,
        onCompare,
        onDuplicate,
        onDelete,
        checkDrawerOpen,
        title: record.name,
      }),
    [onCompare, onDuplicate, onDelete, checkDrawerOpen]
  );

  useEffect(() => {
    if (!objectTypes.length) {
      dispatch(requestObjectTypes());
    }
  }, []);

  const createNewMenu: MenuProps = useMemo(
    () => ({
      items: templateTypeOptions,
      onClick: (info) => {
        onCreate(info.key as EReportTemplateType);
      },
    }),
    [onCreate, templateTypeOptions]
  );

  const footer = useCallback(
    () => (
      <Dropdown menu={createNewMenu} trigger={['click']}>
        <Button size="small" type="primary">
          Create new
        </Button>
      </Dropdown>
    ),
    [createNewMenu]
  );

  const title = useTableTitle<IRowData>({
    total: reportTemplates.length,
    filteredAmount: filterData.length,
  });

  const [contentRef, defaultTableProps] = useDefaultTableProps();

  return (
    <div className="report-template-list" ref={contentRef}>
      <Filter
        defaultFields={defaultFields}
        options={{
          objectType: objectTypeOptions,
          templateType: templateTypeOptions,
        }}
        settings={filterSettings}
        onChange={setFilterSettings}
      />
      <LoadingWrapper isLoading={loading}>
        <EditableTable
          dataSource={filterData}
          columns={columns}
          getMenuProps={menuProps}
          onRow={onRow}
          footer={footer}
          title={title}
          {...defaultTableProps}
        />
      </LoadingWrapper>
      {contextHolder}
    </div>
  );
};

export default memo(DataTable);

const columns = [
  {
    key: 'name',
    dataIndex: 'name',
    title: 'Name',
    ellipsis: true,
    sorter: (item1: IRowData, item2: IRowData) =>
      item1.name.localeCompare(item2.name),
  },
  {
    key: 'templateType',
    dataIndex: 'templateType',
    title: 'Template Type',
    render: (val: string) => getText(val),
    ellipsis: true,
  },
  {
    key: 'objectTypes',
    dataIndex: 'objectTypes',
    title: 'Object Types',
    ellipsis: true,
  },
  {
    key: 'dateRange',
    title: 'Time Period',
    ellipsis: true,
    render: (_, { dateRangeStart, dateRangeEnd, timezone }: IRowData) =>
      `${dayjs(dateRangeStart).tz(timezone).format('YYYY-MM-DD')} - ${dayjs(
        dateRangeEnd
      )
        .tz(timezone)
        .format('YYYY-MM-DD')}`,
  },
];

const defaultFields: TEIFilterField[] = [
  {
    label: 'Template Type',
    key: 'templateType',
    type: TEEFilterType.text,
  },
  {
    label: 'Includes Object Type',
    key: 'objectType',
    type: TEEFilterType.treeSelect,
  },
];
